import { Component, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { AssistantType } from '@vendasta/ai-assistants';
import { GoalInterface } from '@vendasta/ai-assistants/lib/_internal/interfaces/goal.interface';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';
import { AiCapabilitiesTableComponent } from '../ai-capabilities-table/ai-capabilities-table.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'ai-capability-select-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    AiAssistantI18nModule,
    AiCapabilitiesTableComponent,
  ],
  templateUrl: './ai-capability-select-dialog.component.html',
  styleUrls: [
    './ai-capability-select-dialog.component.scss',
    '../../ai-assistant-configuration/ai-assistant-shared.scss',
  ],
})
export class AiCapabilitySelectComponent {
  private readonly dialogRef = inject(MatDialogRef<AiCapabilitySelectComponent>);
  protected readonly excludedGoalIds: string[] = inject(MAT_DIALOG_DATA).excludedGoalIds || [];
  protected readonly assistantType: AssistantType = inject(MAT_DIALOG_DATA).assistantType;
  private readonly snackbar = inject(SnackbarService);

  protected readonly tableSelectedGoals = signal<GoalInterface[]>([]);
  protected readonly translate = inject(TranslateService);

  protected setTableSelectedGoals(goals: GoalInterface[]) {
    this.tableSelectedGoals.set(goals);
  }

  chooseGoals(goals: GoalInterface[]) {
    const conflictingGoals = this.findOverrideConflicts(goals);
    if (conflictingGoals.length > 0) {
      this.snackbar.openErrorSnack(
        this.translate.instant('AI_ASSISTANT.GOALS.ADD_GOAL.ERROR_SELECTING_CONFLICTING_CAPABILITIES', {
          capabilitiesNames: conflictingGoals.map((goal) => goal.name).join(', '),
        }),
      );
      return;
    }

    this.dialogRef.close({
      goals,
    });
  }

  newGoal() {
    this.dialogRef.close({
      newGoal: true,
    });
  }

  private findOverrideConflicts(goals: GoalInterface[]): GoalInterface[] {
    const conflicts: GoalInterface[] = [];

    for (let i = 0; i < goals.length; i++) {
      for (let j = i + 1; j < goals.length; j++) {
        const goalA = goals[i];
        const goalB = goals[j];

        if (this.areGoalsConflicting(goalA, goalB)) {
          if (!conflicts.includes(goalA)) conflicts.push(goalA);
          if (!conflicts.includes(goalB)) conflicts.push(goalB);
        }
      }
    }

    return conflicts;
  }

  private areGoalsConflicting(goalA: GoalInterface, goalB: GoalInterface): boolean {
    const idA = goalA?.id ?? '';
    const idB = goalB?.id ?? '';

    // One goal overrides the other
    if (goalA?.overrideOf?.id === idB || goalB?.overrideOf?.id === idA) {
      return true;
    }

    // Both goals override the same base goal
    if (goalA?.overrideOf?.id && goalB?.overrideOf?.id && goalA.overrideOf.id === goalB.overrideOf.id) {
      return true;
    }

    return false;
  }
}
